import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppInputPassword from '@nxsys/components/AppInputPassword';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppRadioReversed from '@nxsys/components/AppRadioReversed';
import { yesNoOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { ICompany } from '@nxsys/types/models/company';
import { Col, Form, Row } from 'antd';
import clsx from 'clsx';
import { Ref, useEffect, useImperativeHandle, useState } from 'react';
import AppRateInput from '@nxsys/components/AppRateInput';

export enum EmailSettingType {
  DEFAULT = 'DEFAULT',
  CUSTOM = 'CUSTOM',
}

export type EmailSettingsFormRef = {
  submit: () => void;
  getValues: () => Promise<EmailSettingsFormValues>;
};

export type EmailSettingsFormValues = {
  emailSettingType: EmailSettingType;
  emailSender: string;
  smtpHost: string;
  smtpPort: string;
  smtpSecure: boolean | null;
  smtpUser: string;
  smtpPassword: string;
};

type EmailSettingsFormProps = {
  companyData?: ICompany;
  formRef?: Ref<EmailSettingsFormRef>;
  onSubmit?: (values: EmailSettingsFormValues) => void;
  onEmailSettingTypeChange?: (type: EmailSettingType) => void;
};

const EmailSettingsForm: React.FC<EmailSettingsFormProps> = ({
  companyData,
  formRef,
  onSubmit,
  onEmailSettingTypeChange,
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm();
  const [needReinitializeForm, setNeedReinitializeForm] = useState(true);
  const watchValues = Form.useWatch([], form);
  const selectedOption = watchValues?.emailSettingType;

  useImperativeHandle(formRef, () => ({
    submit: () => {
      form.submit();
    },
    getValues: () => {
      return form.validateFields();
    },
  }));

  useEffect(() => {
    if (companyData?.id && needReinitializeForm) {
      form.setFieldsValue({
        emailSettingType: companyData.isUseDefaultEmailSettings === true
          ? EmailSettingType.DEFAULT
          : EmailSettingType.CUSTOM,
        emailSender: companyData.emailSender || '',
        smtpHost: companyData.smtpHost || '',
        smtpPort: companyData.smtpPort || '',
        smtpSecure: companyData.smtpSecure ?? null,
        smtpUser: companyData.smtpUser || '',
        smtpPassword: companyData.smtpPassword || '',
      });
      setNeedReinitializeForm(false);
    }
  }, [companyData?.id]);

  useEffect(() => {
    if (selectedOption) {
      onEmailSettingTypeChange?.(selectedOption as EmailSettingType);
    }
  }, [selectedOption]);

  const onChangeEmailSettingType = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    if (value === EmailSettingType.DEFAULT) {
      form.setFieldsValue({
        emailSettingType: EmailSettingType.DEFAULT,
        emailSender: '',
        smtpHost: '',
        smtpPort: '',
        smtpSecure: null,
        smtpUser: '',
        smtpPassword: '',
      });
    } else {
      form.setFieldsValue({ emailSettingType: EmailSettingType.CUSTOM });
    }
  };

  const isDefault = selectedOption === EmailSettingType.DEFAULT;

  return (
    <AppFormContainer
      form={form}
      onFinish={(values: EmailSettingsFormValues) => onSubmit?.(values)}
      onFinishFailed={(errorInfo: any) => console.error('Failed:', errorInfo)}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      <div className="mb-[16px]">
        <Form.Item name="emailSettingType">
          <AppRadioGroup
            className="flex flex-col gap-y-[12px]"
            onChange={onChangeEmailSettingType}
          >
            <div
              className={clsx(
                'border border-neutral-200 rounded-[12px] p-[16px]',
                {
                  'border-primary-500': isDefault,
                },
              )}
            >
              <AppRadioReversed value={EmailSettingType.DEFAULT}>
                <div className="flex-1">
                  <div className="text-[16px] font-medium mb-[4px]">
                    Use Nxsys's default email settings
                  </div>
                  <div className="text-[14px] text-neutral-600">
                    All correspondence to clients and employees will go from{' '}
                    <span className="text-primary-600">
                      no-reply@nxsys.tech
                    </span>
                  </div>
                </div>
              </AppRadioReversed>
            </div>

            <div
              className={clsx(
                'border border-neutral-200 rounded-[12px] p-[16px]',
                {
                  'border-primary-500':
                    selectedOption === EmailSettingType.CUSTOM,
                },
              )}
            >
              <AppRadioReversed value={EmailSettingType.CUSTOM}>
                <div className="flex-1">
                  <div className="text-[16px] font-medium mb-[4px]">
                    Use custom email settings
                  </div>
                  <div className="text-[14px] text-neutral-600">
                    All correspondence to clients and employees will go from
                    your chosen email
                  </div>
                </div>
              </AppRadioReversed>
            </div>
          </AppRadioGroup>
        </Form.Item>
      </div>

      {!isDefault && (
        <Row gutter={24}>
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.senderEmailId']}
              name="emailSender"
              rules={[
                { required: true, message: messages['form.pleaseEnter'] },
                { type: 'email', message: messages['form.enterValidEmail'] },
              ]}
            >
              <AppInput
                placeholder={messages['form.enter']}
                allowCapitalize={false}
              />
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.smtpHost']}
              name="smtpHost"
              rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
            >
              <AppInput
                placeholder={messages['form.enter']}
                allowCapitalize={false}
              />
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.smtpPort']}
              name="smtpPort"
              rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
            >
              <AppRateInput placeholder={messages['form.enter']} />
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.smtpUser']}
              name="smtpUser"
              rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
            >
              <AppInput
                placeholder={messages['form.enter']}
                allowCapitalize={false}
              />
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.smtpPassword']}
              name="smtpPassword"
            >
              <AppInputPassword
                placeholder={messages['form.enter']}
                type="password"
              />
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.smtpSecure']} name="smtpSecure">
              <AppRadioGroup options={yesNoOptions} />
            </Form.Item>
          </Col>
        </Row>
      )}
    </AppFormContainer>
  );
};

export default EmailSettingsForm;
